import {Anchor} from 'src/design/atoms/Anchor';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React from 'react';
import {Link} from 'react-router-dom';
import {UserValidationStatusEnum} from 'src/api/ProactApiClient';
import {useAppSelector} from 'src/redux/hooks';
import {PathUtility} from 'src/shared/constants/PathBuilder';

export interface InvitationExistingUserMessageProps {
  username: string;
}

export const InvitationExistingUserMessage: React.FC<
  InvitationExistingUserMessageProps
> = ({username}) => {
  const {userAccountStatus} = useAppSelector(state => state.auth);

  const getMessage = () => {
    switch (userAccountStatus) {
      case UserValidationStatusEnum.HasActiveMagicLink:
      case UserValidationStatusEnum.Valid:
        return {
          title: 'Account Already Active',
          message: `This invitation link is for an account with the email address ${username} that is already active. You can sign in to your existing account.`,
          action: 'Sign In',
          link: PathUtility.Login(),
        };

      case UserValidationStatusEnum.Inactive:
        return {
          title: 'Account Inactive',
          message: `This invitation link is for an account with the email address ${username} that is currently inactive. Contact your administrator to unlock your account.`,
          action: 'Contact Support',
          link: PathUtility.Support(),
        };

      case UserValidationStatusEnum.OAuthUnconfirmed:
        return {
          title: 'Account Not Confirmed',
          message: `An account with the email address ${username} already exists but has not been confirmed. Please check your email for a confirmation link.`,
          action: 'Contact Support',
          link: PathUtility.Support(),
        };

      case UserValidationStatusEnum.DoesNotExist:
        return {
          title: 'Account Not Found',
          message: `No account found with the email address ${username}. This invitation link may be invalid or the account may have been deleted.`,
          action: 'Contact Support',
          link: PathUtility.Support(),
        };

      case UserValidationStatusEnum.OAuthArchived:
      case UserValidationStatusEnum.OAuthCompromised:
        return {
          title: 'Account Locked',
          message: `An account with the email address ${username} already exists but has been locked for security reasons. Contact your administrator to resolve the issue.`,
          action: 'Contact Support',
          link: PathUtility.Support(),
        };

      case UserValidationStatusEnum.OAuthResetRequired:
        return {
          title: 'Password Reset Required',
          message: `An account with the email address ${username} already exists but requires a password reset. Please use the forgot password option to reset your password.`,
          action: 'Reset Password',
          link: PathUtility.ForgotPassword(),
        };

      default:
        return {
          title: 'Account Status Unknown',
          message: `An account with the email address ${username} already exists but has an unknown status. Please contact support for assistance.`,
          action: 'Contact Support',
          link: PathUtility.Support(),
        };
    }
  };

  const messageInfo = getMessage();

  return (
    <Stack>
      <Text size="sm" c="dimmed" ta="center">
        {messageInfo.message}
      </Text>

      <Anchor size="sm" component={Link} to={messageInfo.link} ta="center">
        {messageInfo.action} →
      </Anchor>
    </Stack>
  );
};
